import type { Article } from "@/lib/insights/marketing/articles";

/**
 * Flagship article (Technical) — "Industry 4.0 without ripping out your PLCs".
 * Covers Modbus / OPC-UA / S7 and a gateway → twin process flow.
 * Vendor-neutral protocol explainer; product only in the closing block.
 */
export const INDUSTRY_40_ARTICLE: Article = {
  slug: "industry-40-without-ripping-out-your-plcs",
  title: "Industry 4.0 without ripping out your PLCs",
  deck:
    "The fastest way to kill a digitalisation project is to start by replacing controllers that work. Your existing PLCs already speak protocols built for exactly this. Here is how to get plant data into a twin without touching the control layer.",
  goal: "Technical",
  readingMins: 9,
  heroStat: { value: "3", label: "open protocols that cover most of the installed base" },
  blocks: [
    {
      kind: "paragraph",
      lead: true,
      text:
        "There is a persistent and expensive misconception that going digital means tearing out your automation and starting again. It does not. The controllers running your furnaces, mixers and presses were designed to be queried, and most of them already speak one of a small set of industrial protocols. The job is not to replace the control layer — it is to read from it, safely, and carry that data up to a model where it becomes useful. Done right, the PLCs never know the difference.",
    },
    {
      kind: "callout",
      tone: "info",
      title: "The one principle to hold onto",
      text:
        "Monitoring should be read-only and live on its own path. Your control network keeps running the plant; the data path observes it. Get that separation right and digitalisation carries no process risk.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The languages",
      heading: "Three protocols cover most of what you already own",
      sub: "You rarely need more than these to get started — and they are open, not proprietary.",
    },
    {
      kind: "paragraph",
      text:
        "Modbus is the lingua franca of industrial sensors. It is decades old, dead simple, and almost universally supported — if a device has a register, it can probably expose it over Modbus TCP or RTU. It has no built-in security and no rich type system, but for pulling numeric readings off meters, drives and simple controllers it is hard to beat for sheer reach.",
    },
    {
      kind: "paragraph",
      text:
        "OPC-UA is the modern standard built for exactly this era. It is secure by design, carries a structured information model rather than bare registers, and is platform-neutral. If your newer equipment offers an OPC-UA server, prefer it: you get authentication, encryption and self-describing data without bolting those on yourself. It is the protocol most greenfield automation now ships with.",
    },
    {
      kind: "paragraph",
      text:
        "S7 is Siemens' native protocol for the S7-300/400/1200/1500 families that run a large share of the world's plants. Where you have Siemens controllers, reading them over S7 lets you pull data straight from the PLC's data blocks without adding gateways at the device — you meet the controller where it already is.",
    },
    {
      kind: "comparisonBars",
      title: "Illustrative: relative reach vs. richness of the three protocols",
      unit: "/ 10",
      max: 10,
      bars: [
        { label: "Modbus — installed-base reach", value: 9, tone: "brand", display: "very high" },
        { label: "S7 — Siemens-fleet fit", value: 8, tone: "accent", display: "high (Siemens)" },
        { label: "OPC-UA — security + data model", value: 9, tone: "success", display: "very high" },
      ],
      caption:
        "Qualitative, illustrative ranking — not a benchmark. The takeaway: between them, these three reach most equipment without firmware changes.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The bridge",
      heading: "How data travels from a controller to a twin",
    },
    {
      kind: "paragraph",
      text:
        "The piece that connects the two worlds is an edge gateway: a small, on-site service that speaks the plant's protocols on one side and a clean, secure uplink on the other. It polls or subscribes to the tags you care about, normalises them into consistent units and names, buffers them when the network hiccups, and forwards them to wherever the twin lives. Nothing about it requires writing to the PLC.",
    },
    {
      kind: "processFlow",
      title: "Gateway → twin, end to end",
      steps: [
        {
          title: "PLCs & sensors",
          detail: "Your existing controllers expose tags over Modbus, OPC-UA or S7. No firmware change.",
          icon: "cpu",
        },
        {
          title: "Edge gateway",
          detail: "An on-site service reads those tags read-only, normalises units and names, and buffers on dropout.",
          icon: "plug",
        },
        {
          title: "Secure uplink",
          detail: "Readings travel out over an encrypted, outbound-only channel — no inbound ports opened on the plant.",
          icon: "shieldCheck",
        },
        {
          title: "Live twin",
          detail: "Each tag lands on the right entity in the process model, where it drives dashboards, history and root-cause.",
          icon: "gitBranch",
        },
      ],
      caption:
        "The gateway is the only new component — and it observes the line rather than controlling it.",
    },
    {
      kind: "callout",
      tone: "success",
      title: "Why outbound-only matters",
      text:
        "A gateway that only makes outbound connections needs no inbound firewall rules and exposes no listening service to the plant network. The control layer stays sealed; data still flows out. This is the pattern that keeps IT and OT both comfortable.",
    },
    {
      kind: "pullQuote",
      quote:
        "You do not need new controllers to get modern data. You need to read the ones you have — and almost everything you own already knows how to be read.",
      attribution: "The pragmatic path to Industry 4.0",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Sequencing",
      heading: "Start narrow, prove the path, then widen",
    },
    {
      kind: "paragraph",
      text:
        "The mistake is trying to onboard every tag in the plant on day one. The disciplined approach is to pick one line and the dozen or so tags that bear on your worst problem, stand up the gateway for just those, and confirm the data is landing cleanly in the model. Once that path is proven — units right, names consistent, history accumulating — widening it to more tags and more lines is repetition, not invention. The hard part was the first clean reading; everything after is scale.",
    },
    {
      kind: "chart",
      chart: "area",
      title: "Illustrative: tags onboarded as a rollout widens",
      xKey: "week",
      series: [{ dataKey: "tags", label: "Tags streaming", tone: "accent" }],
      data: [
        { week: "W1", tags: 12 },
        { week: "W2", tags: 14 },
        { week: "W4", tags: 38 },
        { week: "W6", tags: 90 },
        { week: "W8", tags: 160 },
        { week: "W12", tags: 320 },
      ],
      caption:
        "Generic, illustrative ramp. The shape reflects the method: a small proven core, then fast expansion once the path is trusted.",
    },
    {
      kind: "paragraph",
      text:
        "Industry 4.0 has a branding problem that makes it sound like a forklift upgrade. The reality for most plants is far more modest and far more achievable: speak the protocols you already have, bridge them with a read-only gateway, and land the data in a model. The controllers stay. The risk stays low. And the data finally gets out of the cabinet and into a place where it can earn its keep.",
    },
    {
      kind: "sahasEnables",
      intro:
        "The protocols and the gateway pattern above are open and vendor-neutral — anyone can build on them. What the platform adds is a gateway that already speaks all three, and a model on the other end ready to receive the data.",
      capabilities: [
        {
          title: "One gateway, three protocols",
          detail:
            "A self-hosted gateway speaks Modbus, OPC-UA and S7 out of the box, with an encrypted outbound-only uplink — no inbound ports on the plant.",
          icon: "plug",
        },
        {
          title: "Tags land on the model",
          detail:
            "Onboard a PLC in minutes and map its tags straight onto entities in your process twin, where they drive dashboards and alarms.",
          icon: "gitBranch",
        },
        {
          title: "Secure by default",
          detail:
            "Read-only acquisition, encrypted transport and a data path kept separate from control — built to satisfy both IT and OT.",
          icon: "shieldCheck",
        },
      ],
    },
  ],
};
